import { useMemo, useState } from 'react';
import {
  FolderPlus, Plus, Tag, Users, Trash2, Pencil, Eye, Download, FileText,
  ChevronRight, ChevronDown, Folder as FolderIcon,
} from 'lucide-react';
import { FaBookOpen, FaEnvelope } from '../ui/FaIcons';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import {
  useCabinetFolders, useCabinetFiles, useCabinetTags, useAllCabinetFileTags,
  useCreateCabinetFolder, useRenameCabinetFolder, useDeleteCabinetFolder,
  useDeleteCabinetFile, useRenameCabinetFile,
} from '../../hooks/queries/useCabinetLibrary';
import {
  resolveRootFolder, getDownloadUrl,
  type CabinetFolder, type CabinetFile,
} from '../../services/cabinet/cabinetService';
import { formatFileSize, isPreviewable } from '../../services/files/fileUtils';
import { CabinetDropzone } from './CabinetDropzone';
import { CabinetFilePreviewModal } from './CabinetFilePreviewModal';
import { CabinetFileTagsModal } from './CabinetFileTagsModal';
import { CabinetTagsManagerModal } from './CabinetTagsManagerModal';
import { CabinetMembersModal } from './CabinetMembersModal';
import { LetterModelsShelf } from './LetterModelsShelf';

import { formatAppDate } from '../../lib/dateFormat';
function FolderNode({
  folder, allFolders, currentId, onSelect, expanded, onToggle,
}: {
  folder: CabinetFolder;
  allFolders: CabinetFolder[];
  currentId: string | null;
  onSelect: (f: CabinetFolder) => void;
  expanded: Set<string>;
  onToggle: (id: string) => void;
}) {
  const children = allFolders.filter((f) => f.parent_id === folder.id);
  const isOpen = expanded.has(folder.id);
  const isActive = currentId === folder.id;

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1 rounded text-sm cursor-pointer ${
          isActive ? 'bg-cime-primary/10 text-cime-primary font-medium' : 'hover:bg-neutral-100'
        }`}
      >
        {children.length > 0 ? (
          <button
            type="button"
            onClick={() => onToggle(folder.id)}
            className="p-0.5 rounded hover:bg-neutral-200"
            aria-label={isOpen ? 'Réduire' : 'Développer'}
          >
            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <button
          type="button"
          onClick={() => onSelect(folder)}
          className="flex-1 flex items-center gap-1.5 text-left truncate"
        >
          <FolderIcon className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
          <span className="truncate">{folder.name}</span>
        </button>
      </div>
      {isOpen && children.length > 0 && (
        <div className="ml-4 border-l border-neutral-200 pl-1">
          {children.map((c) => (
            <FolderNode
              key={c.id}
              folder={c}
              allFolders={allFolders}
              currentId={currentId}
              onSelect={onSelect}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CabinetLibraryPage() {
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);
  const isAdmin = role === 'admin';
  const canEdit = role ? canAccessFeature(role, 'cabinet_library') : false;

  const { data: folders = [] } = useCabinetFolders();
  const { data: tags = [] } = useCabinetTags();
  const { data: allFileTags = [] } = useAllCabinetFileTags();

  const [currentId, setCurrentId] = useState<string | null>(null);
  const [showLetters, setShowLetters] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);

  const [creatingRoot, setCreatingRoot] = useState(false);
  const [creatingSubFor, setCreatingSubFor] = useState<CabinetFolder | null>(null);
  const [renameTarget, setRenameTarget] = useState<CabinetFolder | null>(null);
  const [newFolderName, setNewFolderName] = useState('');

  const [previewFile, setPreviewFile] = useState<CabinetFile | null>(null);
  const [tagsForFile, setTagsForFile] = useState<CabinetFile | null>(null);
  const [showTagsManager, setShowTagsManager] = useState(false);
  const [membersFor, setMembersFor] = useState<CabinetFolder | null>(null);
  const [renameFile, setRenameFile] = useState<CabinetFile | null>(null);
  const [renameFileName, setRenameFileName] = useState('');

  const createFolder = useCreateCabinetFolder();
  const renameFolderMut = useRenameCabinetFolder();
  const deleteFolderMut = useDeleteCabinetFolder();

  const currentFolder = folders.find((f) => f.id === currentId) ?? null;
  const rootFolder = currentFolder ? resolveRootFolder(currentFolder.id, folders) : null;
  const roots = folders.filter((f) => f.is_root);

  const { data: files = [] } = useCabinetFiles(currentId);
  const deleteFileMut = useDeleteCabinetFile(currentId ?? '');
  const renameFileMut = useRenameCabinetFile(currentId ?? '');

  // Index : fileId → tagIds
  const tagsByFileId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of allFileTags) {
      const arr = map.get(r.file_id) ?? [];
      arr.push(r.tag_id);
      map.set(r.file_id, arr);
    }
    return map;
  }, [allFileTags]);

  const tagById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);

  const filteredFiles = activeTagFilter
    ? files.filter((f) => (tagsByFileId.get(f.id) ?? []).includes(activeTagFilter))
    : files;

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSelectFolder(f: CabinetFolder) {
    setShowLetters(false);
    setCurrentId(f.id);
    setActiveTagFilter(null);
    if (!expanded.has(f.id)) {
      setExpanded((prev) => new Set(prev).add(f.id));
    }
  }

  async function handleCreateFolder() {
    if (!newFolderName.trim()) return;
    const isRoot = creatingRoot;
    const parentId = isRoot ? null : creatingSubFor?.id ?? null;
    if (!isRoot && !parentId) return;
    const created = await createFolder.mutateAsync({
      parentId,
      name: newFolderName,
      isRoot,
      createdBy: collaborator?.id ?? null,
    });
    setNewFolderName('');
    setCreatingRoot(false);
    setCreatingSubFor(null);
    setCurrentId(created.id);
  }

  async function handleRenameFolder() {
    if (!renameTarget || !newFolderName.trim()) return;
    await renameFolderMut.mutateAsync({ folderId: renameTarget.id, name: newFolderName });
    setRenameTarget(null);
    setNewFolderName('');
  }

  async function handleDeleteFolder(f: CabinetFolder) {
    const label = f.is_root ? 'ce dossier racine' : `le dossier « ${f.name} »`;
    if (!window.confirm(`Supprimer ${label} ? Il doit être vide.`)) return;
    try {
      await deleteFolderMut.mutateAsync(f.id);
      if (currentId === f.id) setCurrentId(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur');
    }
  }

  async function handleDownload(file: CabinetFile) {
    try {
      const url = await getDownloadUrl(file.id);
      window.open(url, '_blank');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur téléchargement');
    }
  }

  async function handleDeleteFile(file: CabinetFile) {
    if (!window.confirm(`Supprimer « ${file.filename} » ?`)) return;
    try {
      await deleteFileMut.mutateAsync(file.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur');
    }
  }

  async function handleRenameFile() {
    if (!renameFile || !renameFileName.trim()) return;
    await renameFileMut.mutateAsync({ fileId: renameFile.id, filename: renameFileName });
    setRenameFile(null);
    setRenameFileName('');
  }

  return (
    <div className="flex flex-col h-[calc(100vh-112px)] overflow-hidden">
      <div className="flex flex-1 min-h-0 overflow-hidden px-8 pt-8 pb-8 gap-4">
        {/* Arbre des dossiers */}
        <aside className="w-72 flex-shrink-0 flex flex-col border border-neutral-200 rounded-xl bg-white overflow-hidden">
          {/* Actions bibliothèque */}
          <div className="px-3 py-3 border-b border-neutral-200 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setShowTagsManager(true)}
              className="flex h-9 items-center justify-center gap-1.5 rounded-full border border-cime-primary/25 px-3.5 text-[13px] font-medium text-cime-primary hover:border-cime-primary disabled:opacity-50"
              disabled={!isAdmin}
              title={isAdmin ? 'Gérer les tags' : 'Réservé aux administrateurs'}
            >
              <Tag className="h-3.5 w-3.5" /> Tags
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => { setCreatingRoot(true); setNewFolderName(''); }}
                className="flex h-9 items-center justify-center gap-1.5 rounded-full bg-cime-primary px-3.5 text-[13px] font-medium text-white hover:opacity-90"
              >
                <FolderPlus className="h-3.5 w-3.5" /> Nouveau dossier racine
              </button>
            )}
          </div>
          <div className="px-2 py-2 border-b border-neutral-200">
            <button
              type="button"
              onClick={() => { setShowLetters(true); setCurrentId(null); }}
              className={`flex w-full items-center gap-2 rounded-md border-l-2 px-2 py-1.5 text-left text-[13px] ${showLetters && !currentFolder ? 'border-cime-primary bg-cime-primary/[0.07] font-semibold text-cime-primary' : 'border-transparent text-neutral-700 hover:bg-neutral-50'}`}
            >
              <FaEnvelope className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              Courriers types
            </button>
          </div>
          <div className="px-3 py-2 border-b border-neutral-200 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">Dossiers</span>
          </div>
          <div className="flex-1 overflow-y-auto py-2 px-2">
            {roots.length === 0 ? (
              <p className="text-xs text-neutral-500 italic px-2 py-3">
                Aucun dossier racine. {isAdmin && 'Cliquez sur « Nouveau dossier racine ».'}
              </p>
            ) : (
              roots.map((r) => (
                <FolderNode
                  key={r.id}
                  folder={r}
                  allFolders={folders}
                  currentId={currentId}
                  onSelect={handleSelectFolder}
                  expanded={expanded}
                  onToggle={toggleExpand}
                />
              ))
            )}
          </div>
        </aside>

        {/* Contenu */}
        <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden border border-neutral-200 rounded-xl bg-white">
          {showLetters && !currentFolder ? (
            <LetterModelsShelf />
          ) : !currentFolder ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-2xl bg-cime-primary/10 flex items-center justify-center mb-4">
                <FaBookOpen className="w-8 h-8 text-cime-primary" />
              </div>
              <h2 className="text-base font-semibold text-neutral-800 mb-1">Bibliothèque du cabinet</h2>
              <p className="text-sm text-neutral-500 max-w-xs">
                Sélectionnez un dossier à gauche pour consulter ou déposer des fichiers.
              </p>
            </div>
          ) : (
            <>
              {/* Header dossier courant */}
              <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200">
                <div className="min-w-0">
                  <h2 className="font-semibold truncate flex items-center gap-2">
                    <FolderIcon className="w-4 h-4 opacity-60" />
                    {currentFolder.name}
                  </h2>
                  {rootFolder && currentFolder.id !== rootFolder.id && (
                    <p className="text-xs text-neutral-500 mt-0.5">dans {rootFolder.name}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => { setCreatingSubFor(currentFolder); setNewFolderName(''); }}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-neutral-100"
                      title="Nouveau sous-dossier"
                    >
                      <Plus className="w-3.5 h-3.5" /> Sous-dossier
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => { setRenameTarget(currentFolder); setNewFolderName(currentFolder.name); }}
                      className="p-1.5 rounded hover:bg-neutral-100 text-neutral-600"
                      title="Renommer"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isAdmin && currentFolder.is_root && (
                    <button
                      type="button"
                      onClick={() => setMembersFor(currentFolder)}
                      className="p-1.5 rounded hover:bg-neutral-100 text-neutral-600"
                      title="Gérer les accès"
                    >
                      <Users className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => handleDeleteFolder(currentFolder)}
                      className="p-1.5 rounded hover:bg-red-50 text-neutral-600 hover:text-red-600"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </header>

              {/* Tag filter chips */}
              {tags.length > 0 && (
                <div className="px-4 py-2 border-b border-neutral-100 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-neutral-500">Filtrer :</span>
                  <button
                    type="button"
                    onClick={() => setActiveTagFilter(null)}
                    className={`text-xs px-2 py-0.5 rounded-full border ${
                      activeTagFilter === null
                        ? 'bg-neutral-800 text-white border-neutral-800'
                        : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    Tous
                  </button>
                  {tags.map((t) => {
                    const on = activeTagFilter === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setActiveTagFilter(on ? null : t.id)}
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          on ? 'text-white' : 'text-neutral-600 hover:bg-neutral-50'
                        }`}
                        style={
                          on
                            ? { backgroundColor: t.color, borderColor: t.color }
                            : { borderColor: t.color }
                        }
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Dropzone + liste fichiers */}
              <div className="flex-1 overflow-y-auto p-4">
                {rootFolder && canEdit && (
                  <CabinetDropzone folderId={currentFolder.id} rootFolderId={rootFolder.id}>
                    <FileList
                      files={filteredFiles}
                      tagsByFileId={tagsByFileId}
                      tagById={tagById}
                      onPreview={setPreviewFile}
                      onDownload={handleDownload}
                      onRename={(f) => { setRenameFile(f); setRenameFileName(f.filename); }}
                      onDelete={handleDeleteFile}
                      onEditTags={setTagsForFile}
                      canEdit={canEdit}
                    />
                  </CabinetDropzone>
                )}
                {(!rootFolder || !canEdit) && (
                  <FileList
                    files={filteredFiles}
                    tagsByFileId={tagsByFileId}
                    tagById={tagById}
                    onPreview={setPreviewFile}
                    onDownload={handleDownload}
                    onRename={(f) => { setRenameFile(f); setRenameFileName(f.filename); }}
                    onDelete={handleDeleteFile}
                    onEditTags={setTagsForFile}
                    canEdit={false}
                  />
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Modales */}
      {(creatingRoot || creatingSubFor) && (
        <PromptModal
          title={creatingRoot ? 'Nouveau dossier racine' : `Nouveau sous-dossier de « ${creatingSubFor!.name} »`}
          placeholder="Nom du dossier"
          value={newFolderName}
          onChange={setNewFolderName}
          onCancel={() => { setCreatingRoot(false); setCreatingSubFor(null); setNewFolderName(''); }}
          onSubmit={handleCreateFolder}
          submitting={createFolder.isPending}
          submitLabel="Créer"
        />
      )}
      {renameTarget && (
        <PromptModal
          title={`Renommer « ${renameTarget.name} »`}
          placeholder="Nouveau nom"
          value={newFolderName}
          onChange={setNewFolderName}
          onCancel={() => { setRenameTarget(null); setNewFolderName(''); }}
          onSubmit={handleRenameFolder}
          submitting={renameFolderMut.isPending}
          submitLabel="Renommer"
        />
      )}
      {renameFile && (
        <PromptModal
          title={`Renommer le fichier`}
          placeholder="Nouveau nom"
          value={renameFileName}
          onChange={setRenameFileName}
          onCancel={() => { setRenameFile(null); setRenameFileName(''); }}
          onSubmit={handleRenameFile}
          submitting={renameFileMut.isPending}
          submitLabel="Renommer"
        />
      )}
      {previewFile && <CabinetFilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
      {tagsForFile && <CabinetFileTagsModal file={tagsForFile} onClose={() => setTagsForFile(null)} />}
      {showTagsManager && <CabinetTagsManagerModal onClose={() => setShowTagsManager(false)} />}
      {membersFor && <CabinetMembersModal rootFolder={membersFor} onClose={() => setMembersFor(null)} />}
    </div>
  );
}

// ─── Sous-composants internes ────────────────────────────────────────────

function FileList({
  files, tagsByFileId, tagById, onPreview, onDownload, onRename, onDelete, onEditTags, canEdit,
}: {
  files: CabinetFile[];
  tagsByFileId: Map<string, string[]>;
  tagById: Map<string, { id: string; name: string; color: string }>;
  onPreview: (f: CabinetFile) => void;
  onDownload: (f: CabinetFile) => void;
  onRename: (f: CabinetFile) => void;
  onDelete: (f: CabinetFile) => void;
  onEditTags: (f: CabinetFile) => void;
  canEdit: boolean;
}) {
  if (files.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-neutral-500">
        <FileText className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
        Aucun fichier ici.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-neutral-100">
      {files.map((f) => {
        const tagIds = tagsByFileId.get(f.id) ?? [];
        const previewable = isPreviewable(f.mime);
        return (
          <li key={f.id} className="flex items-center gap-3 py-2 px-2 hover:bg-neutral-50 rounded group">
            <FileText className="w-5 h-5 text-neutral-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium truncate">{f.filename}</span>
                {tagIds.map((tid) => {
                  const t = tagById.get(tid);
                  if (!t) return null;
                  return (
                    <span
                      key={tid}
                      className="text-[10px] px-1.5 py-0 rounded-full text-white"
                      style={{ backgroundColor: t.color }}
                    >
                      {t.name}
                    </span>
                  );
                })}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {formatFileSize(f.size)} · {formatAppDate(f.created_at)}
              </div>
            </div>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
              {previewable && (
                <button
                  type="button"
                  onClick={() => onPreview(f)}
                  className="p-1.5 rounded hover:bg-neutral-200 text-neutral-600"
                  title="Aperçu"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onDownload(f)}
                className="p-1.5 rounded hover:bg-neutral-200 text-neutral-600"
                title="Télécharger"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
              {canEdit && (
                <>
                  <button
                    type="button"
                    onClick={() => onEditTags(f)}
                    className="p-1.5 rounded hover:bg-neutral-200 text-neutral-600"
                    title="Tags"
                  >
                    <Tag className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRename(f)}
                    className="p-1.5 rounded hover:bg-neutral-200 text-neutral-600"
                    title="Renommer"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(f)}
                    className="p-1.5 rounded hover:bg-red-50 text-neutral-600 hover:text-red-600"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PromptModal({
  title, placeholder, value, onChange, onCancel, onSubmit, submitting, submitLabel,
}: {
  title: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-md shadow-xl w-full max-w-sm"
      >
        <header className="px-5 py-3 border-b border-neutral-200">
          <h2 className="font-semibold text-sm">{title}</h2>
        </header>
        <div className="px-5 py-4">
          <input
            autoFocus
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={100}
            className="w-full rounded border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-cime-primary"
          />
        </div>
        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <button type="button" onClick={onCancel} disabled={submitting} className="px-3 py-1.5 text-sm rounded hover:bg-neutral-100">
            Annuler
          </button>
          <button
            type="submit"
            disabled={!value.trim() || submitting}
            className="px-3 py-1.5 text-sm rounded bg-cime-primary text-white disabled:opacity-50"
          >
            {submitting ? '…' : submitLabel}
          </button>
        </footer>
      </form>
    </div>
  );
}
